import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { Button } from '~common/components/button'
import { Card } from '~common/components/card'
import type { PricingComponentDependencies } from './logic'
import { PricingComponentLogic } from './logic'
import { Icon } from '~common/components/icon'
import { useAppContext } from '~/app-context'
import { LoadingIndicator } from '~common/components/loading'
import PricingDisplay from '~common/components/pricing-display'

export default function PricingComponent(props: PricingComponentDependencies) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(PricingComponentLogic, {
        ...props,
        ...appContext,
    })

    return (
        <Outer>
            <Container>
                <HeaderSection>
                    <Title>
                        {state.titles.title.split('\n').map((line, index) => (
                            <React.Fragment key={index}>
                                {line}
                                {index !==
                                    state.titles.title.split('\n').length -
                                        1 && <br />}
                            </React.Fragment>
                        ))}
                    </Title>
                    <Subtitle>
                        {state.titles.subtitle}
                        {/* {state.showCountdown && state.offerCountdownText && (
                            <Countdown>
                                <span>Offer ends in</span>
                                <CountDownText>
                                    {state.offerCountdownText}
                                </CountDownText>
                            </Countdown>
                        )} */}
                    </Subtitle>
                </HeaderSection>

                {state.error && <ErrorMessage>{state.error}</ErrorMessage>}

                <PricingDisplay
                    isDev={process.env.NODE_ENV === 'development'}
                    selectPackage={logic.selectPackage}
                    isUpgrading={state.isUpgrading}
                    discountBannerText={state.discountBannerText}
                />

                <MoreQuestionsContainer>
                    <SupportTitle>Any Questions?</SupportTitle>
                    <Button
                        label="Chat with Support"
                        variant="secondary"
                        icon="chat-dots"
                        size="large"
                        onClick={() => {
                            if (window.$crisp) {
                                window.$crisp.push(['do', 'chat:open'])
                            }
                        }}
                    />
                </MoreQuestionsContainer>
            </Container>
        </Outer>
    )
}

const Outer = styled.div`
    height: 100%;
`

const Container = styled.div`
    padding: ${({ theme }) => theme.spacing[8]};
    box-sizing: border-box;
    width: 100%;
    text-align: center;
`

const HeaderSection = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing[16]};
    position: relative;
    z-index: 2;
`

const Title = styled.h1`
    line-height: 1.5;
    font-weight: ${({ theme }) => theme.typography.fontWeight.extrabold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
    font-size: ${({ theme }) => theme.typography.fontSize['5xl']};
    white-space: pre-line;
`

const Subtitle = styled.p`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: ${({ theme }) => theme.spacing[4]} auto;
    max-width: 600px;
`

const ErrorMessage = styled.div`
    background-color: ${({ theme }) => theme.colors.error[50]};
    color: ${({ theme }) => theme.colors.error[700]};
    padding: ${({ theme }) => theme.spacing[3]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    text-align: center;
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
`

const SupportTitle = styled.h3`
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0 0 ${({ theme }) => theme.spacing[4]} 0;
    text-align: center;
`

const MoreQuestionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    margin-top: ${({ theme }) => theme.spacing[10]};
`
